<template>
  <div class="w-full flex">
    <div class="relative w-full">
      <v-calendar
        class="custom-calendar max-w-full z-20"
        :class="[
          active_calendar_day ? 'custom-calendar-overlay' : '',
          cloud ? 'cloud-calendar' : '',
        ]"
        :masks="masks"
        :attributes="posts"
        disable-page-swipe
        is-expanded
      >
        <template #day-content="{ day, attributes }">
          <div
            class="flex flex-col h-full z-10 overflow-hidden relative cursor-pointer"
            @click="highlightDay(day)"
          >
            <!-- Event Range -->
            <div
              :class="'bg-' + primary_colour"
              class="absolute z-20 left-0 top-0 event-range transition-all h-full w-full pointer-events-none"
            ></div>

            <!-- Active Day -->
            <div
              :class="[
                active_calendar_day && active_calendar_day.date === day.id
                  ? 'opacity-100'
                  : 'opacity-0',
                'bg-' + primary_colour,
              ]"
              class="absolute left-0 top-0 transition-all h-2 w-full pointer-events-none"
            ></div>

            <!-- End date -->
            <div
              :class="'bg-' + primary_colour"
              class="event-end-date w-full h-1/4 absolute bottom-0 left-0 z-50 text-white flex items-center justify-center pointer-events-none transition-all"
            >
              <h3 class="w-full text-center text-xs m-0">Event End</h3>
            </div>

            <!-- Overflow -->
            <div
              v-if="attributes && attributes.length > 3"
              class="w-full flex items-center justify-center h-4 z-10 absolute bottom-0 bg-white left-0 text-center text-xs"
            >
              <!-- + {{ attributes.length - 3 }} -->
              <div
                class="flex items-center justify-center transition-all space-x-2 group p-1"
              >
                <div
                  :class="'group-hover:bg-' + primary_colour"
                  class="w-2 h-2 rounded-full bg-truegray-300 transition-all"
                ></div>
                <div
                  :class="'group-hover:bg-' + primary_colour"
                  class="w-2 h-2 rounded-full bg-truegray-300 transition-all"
                ></div>
                <div
                  :class="'group-hover:bg-' + primary_colour"
                  class="w-2 h-2 rounded-full bg-truegray-300 transition-all"
                ></div>
              </div>
            </div>

            <!-- Day # -->
            <span
              :class="[
                active_calendar_day && active_calendar_day.date === day.id
                  ? 'text-' + primary_colour + ' font-bold active'
                  : 'text-gray-900',
              ]"
              class="day-label relative transform transition-all text-xs px-2 pt-2"
            >
              {{ day.day }}
            </span>

            <!-- Day contents -->
            <div
              class="flex-grow overflow-y-auto overflow-x-auto no-scrollbar mb-4"
            >
              <div
                v-for="attr in attributes"
                :key="attr.id"
                class="calendar-event text-xs leading-tight rounded-sm px-1 pt-1 cursor-pointer hover:bg-truegray-100"
                @mouseenter="checkEventFinish(attr)"
                @mouseleave="removeEventFinish(attr)"
                @click="showEventDetails(attr)"
              >
                <span class="event-title">
                  {{ attr.customData.title }}
                </span>
              </div>
            </div>
          </div>
        </template>
      </v-calendar>

      <div
        :class="[
          active_calendar_event ? 'w-1/2' : 'w-1/4',
          active_calendar_day
            ? 'opacity-100 transition-all active'
            : 'pointer-events-none opacity-0',
        ]"
        class="z-30 absolute calendar-modal flex"
      >
        <!-- Event State -->
        <div
          v-if="active_calendar_event"
          :class="active_calendar_event ? 'w-1/2' : 'w-full'"
          class="bg-white h-full"
        >
          <div
            :style="{ backgroundImage: 'url(' + featured_image + ')' }"
            class="bg-cover bg-center relative h-48 bg-truegray-700 w-full text-white flex items-center justify-center flex-col"
          >
            <!--
              <div @click="removeEventDetails" class="absolute left-4 top-4 w-6 h-6 rounded-full bg-white cursor-pointer group text-black flex items-center justify-center border border-truegray-700">
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-3 h-3">
              <path stroke-linecap="round" stroke-linejoin="round" d="M10.5 19.5L3 12m0 0l7.5-7.5M3 12h18" />
              </svg>
              </div> 
            -->
          </div>
          <div class="p-8">
            <h3>{{ active_calendar_event.customData.title }}</h3>
            <PostTags
              v-if="
                active_calendar_event.customData.general_tags &&
                active_calendar_event.customData.general_tags.length > 0
              "
              :tags="active_calendar_event.customData.general_tags"
            />
            <div class="border-t border-truegray-300 pt-3 text-xs">
              <!-- Dates -->
              <div
                v-if="active_calendar_event.customData.event_start_at"
                class="mb-2 flex items-center"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  class="h-5 w-5 mr-2"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path
                    fill-rule="evenodd"
                    d="M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zm0 5a1 1 0 000 2h8a1 1 0 100-2H6z"
                    clip-rule="evenodd"
                  />
                </svg>
                {{ active_calendar_event.customData.event_start_at_ui }}
                <span
                  v-if="
                    active_calendar_event.customData.event_end_at_ui &&
                    active_calendar_event.customData.event_start_at_ui !=
                      active_calendar_event.customData.event_end_at_ui
                  "
                  >-
                  {{ active_calendar_event.customData.event_end_at_ui }}</span
                >
              </div>
              <!-- /Dates -->

              <!-- Venue Title -->
              <div
                v-if="active_calendar_event.customData.event_venue_title"
                class="mb-2 flex items-center text-xs"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  class="h-5 w-5 mr-2"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path
                    fill-rule="evenodd"
                    d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z"
                    clip-rule="evenodd"
                  />
                </svg>
                {{ active_calendar_event.customData.event_venue_title }}
              </div>
              <!-- /Venue Title -->
              <div class="mt-auto border-t border-truegray-300 pt-3">
                <h6
                  v-if="
                    active_calendar_event.customData.region_tag &&
                    active_calendar_event.customData.region_tag.length > 0 &&
                    cloud
                  "
                  class=""
                >
                  Region:
                  <span class="text-cw-alt font-semibold ml-2">{{
                    active_calendar_event.customData.region_tag[0]
                  }}</span>
                </h6>
                <h6 class="text-truegray-400 mb-3">
                  Published on
                  {{ active_calendar_event.customData.created_at_ui }}
                </h6>
                <a
                  :id="active_calendar_event.customData.id"
                  class="z-40 pb-4 hover:text-cw-alt"
                  @click="viewPost(active_calendar_event.customData.id)"
                  >View Event ></a
                >
              </div>
            </div>
          </div>
        </div>

        <!-- Day State -->
        <div
          v-if="active_calendar_day"
          :class="active_calendar_event ? 'w-1/2' : 'w-full'"
          class="h-full bg-white border-truegray-300 border text-black overflow-auto pb-4"
        >
          <div
            class="absolute left-6 top-6 w-6 h-6 rounded-full bg-white border-truegray-700 border cursor-pointer group text-truegray-700 flex items-center justify-center"
            @click="removeFocussedDay"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              stroke-width="1.5"
              stroke="currentColor"
              class="w-3 h-3"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                d="M10.5 19.5L3 12m0 0l7.5-7.5M3 12h18"
              />
            </svg>
          </div>
          <div class="w-full text-right pr-4 pt-12 mb-8">
            <h6 class="">Events starting on</h6>
            <h2 class="">{{ active_day_for_ui }}</h2>
          </div>

          <div v-for="post in active_calendar_day.posts" :key="post.id">
            <div
              class="relative hover:bg-truegray-100 my-4 py-2 px-8 cursor-pointer transition-all flex items-center"
              :class="
                active_calendar_event &&
                active_calendar_event.customData.id === post.customData.id
                  ? 'bg-truegray-100'
                  : 'bg-white'
              "
              @mouseenter="checkEventFinish(post)"
              @mouseleave="removeEventFinish(post)"
              @click="showEventDetails(post)"
            >
              <div
                class="flex flex-col border-2 transition-all p-1 items-center justify-center mr-4 flex-shrink-0"
                :class="[
                  active_calendar_event &&
                  active_calendar_event.customData.id === post.customData.id &&
                  cloud
                    ? 'cloud-event-active'
                    : '',
                  active_calendar_event &&
                  active_calendar_event.customData.id === post.customData.id
                    ? 'bg-' +
                      primary_colour +
                      ' text-white border-' +
                      primary_colour
                    : 'bg-white text-black border-truegray-300',
                  'hover:border-' + primary_colour,
                ]"
              >
                <h6
                  :class="
                    active_calendar_event &&
                    active_calendar_event.customData.id === post.customData.id
                      ? 'text-white'
                      : 'text-' + primary_colour
                  "
                  class="m-0"
                >
                  Ends On
                </h6>
                <h3 class="m-0">{{ post.customData.calendar_end_day }}</h3>
                <h6 class="m-0">{{ post.customData.calendar_end_month }}</h6>
              </div>

              <!--
                <h6 class="w-full flex items-center relative pl-2 date-circle">
                <span class="mr-2">{{post.customData.event_start_at_ui}}</span>
                <span class="flex mr-2 items-center" v-if="post.customData.event_end_at_ui && post.customData.event_end_at_ui !== post.customData.event_start_at_ui"> 
                <span class="text-gray-400 text-sm mr-2">to</span> 
                <span>{{post.customData.event_end_at_ui}}</span>
                </span>
                </h6> 
              -->
              <div class="flex transition-all relative">
                <h3 class="text-lg m-0">{{ post.customData.title }}</h3>
              </div>
            </div>
          </div>
          <div
            v-if="active_calendar_day && !active_calendar_day.posts.length > 0"
          >
            <p class="px-6 pt-4 italic">No events today. Put your feet up!</p>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  props: {
    posts: { default: [] },
    cloud: { default: false },
  },

  data() {
    return {
      calendar: false,
      masks: {
        weekdays: 'WWW',
      },

      featured_image: null,

      // Right hand side column data
      active_calendar_day: null,
      active_calendar_event: null,
      active_day_for_ui: null,
      primary_colour: null,
      // text_colour : null,
      // border_colour: null,
    }
  },

  watch: {
    active_calendar_day: {
      handler: function (val) {
        if (val) {
          let date = new Date(val.date)
          let day = date.getDate()
          let suffix = this.getDaySuffix(day)
          let month = date.toLocaleString('default', { month: 'short' })
          let formattedDate = `${day}${suffix} ${month}`
          this.$set(this, 'active_day_for_ui', formattedDate)
          if (val.posts && val.posts.length > 0) {
            this.$set(this, 'active_calendar_event', val.posts[0])
          }
        }
      },

      deep: true,
    },

    active_calendar_event: {
      handler: function (val) {
        if (val) {
          if (val.customData.event_start_at_ui) {
            this.start_date_short =
              val.customData.event_start_at_ui.split(',')[0]
            this.end_date_short = val.customData.event_end_at_ui.split(',')[0]
          }
          if (val.customData.image_featured) {
            this.$set(this, 'featured_image', val.customData.image_featured)
          } else {
            switch (val.customData.category) {
              case 1:
                this.$set(this, 'featured_image', '/img/default-news.jpg')
                break
              case 2:
                this.$set(this, 'featured_image', '/img/default-job.png')
                break
              case 3:
                this.$set(this, 'featured_image', '/img/default-event.png')
                break

              default:
                break
            }
          }
        }
      },

      deep: true,
    },
  },

  mounted() {
    if (this.cloud) {
      this.$set(this, 'primary_colour', 'cw-alt')
    } else {
      this.$set(this, 'primary_colour', 'primary')
    }
  },

  methods: {
    viewPost(id) {
      let postPath = `/cloudpost/${id}`
      if (!this.cloud) {
        postPath = `/post/${id}`
      }
      this.$router.push({ path: postPath })
    },

    checkEventFinish(post) {
      // Create array of all dates the event is active on
      let datesBetween = this.getDatesBetween(
        post.customData.event_start_at,
        post.customData.event_end_at,
      )

      //Convert them into a string to pass to querySelectorAll
      let selectorString = ''
      datesBetween.forEach((date) => {
        selectorString += '.id-' + date + ','
      })

      // Remove trailing comma for last element
      let elementsBetween = document.querySelectorAll(
        selectorString.slice(0, -1),
      )

      if (elementsBetween) {
        elementsBetween.forEach((element) => {
          element.classList.add('range-active')
        })
      }

      if (post.customData.event_start_at !== post.customData.event_end_at) {
        // find out if our end date is in the same month as our start date
        if (
          this.areDatesInSameMonth(
            post.customData.event_start_at,
            post.customData.event_end_at,
          )
        ) {
          let endDateDivArr = document.getElementsByClassName(
            'id-' + post.customData.event_end_at,
          )
          let endDateDiv = endDateDivArr[0]
          endDateDiv.classList.add('end-date-active')
        }
      }
    },

    areDatesInSameMonth(startDate, endDate) {
      let date1 = new Date(startDate)
      let date2 = new Date(endDate)
      return (
        date1.getMonth() === date2.getMonth() &&
        date1.getFullYear() === date2.getFullYear()
      )
    },

    getDatesBetween(startDate, endDate) {
      const dates = []
      let currentDate = new Date(startDate)
      let stopDate = new Date(endDate)
      while (currentDate <= stopDate) {
        dates.push(currentDate.toISOString().slice(0, 10))
        currentDate.setDate(currentDate.getDate() + 1)
      }

      return dates
    },

    removeEventFinish(post) {
      // Create array of all dates the event is active on
      let datesBetween = this.getDatesBetween(
        post.customData.event_start_at,
        post.customData.event_end_at,
      )

      //Convert them into a string to pass to querySelectorAll
      let selectorString = ''
      datesBetween.forEach((date) => {
        selectorString += '.id-' + date + ','
      })

      // Remove trailing comma for last element
      let elementsBetween = document.querySelectorAll(
        selectorString.slice(0, -1),
      )
      if (elementsBetween) {
        elementsBetween.forEach((element) => {
          element.classList.remove('range-active')
        })
      }
      if (post.customData.event_start_at !== post.customData.event_end_at) {
        // find out if our end date is in the same month as our start date
        if (
          this.areDatesInSameMonth(
            post.customData.event_start_at,
            post.customData.event_end_at,
          )
        ) {
          let endDateDivArr = document.getElementsByClassName(
            'id-' + post.customData.event_end_at,
          )
          let endDateDiv = endDateDivArr[0]
          endDateDiv.classList.remove('end-date-active')
        }
      }
    },

    showEventDetails(post) {
      this.$set(this, 'active_calendar_event', post)
    },

    removeEventDetails() {
      this.$set(this, 'active_calendar_event', null)
    },

    removeFocussedDay() {
      this.$set(this, 'active_calendar_day', null)
      this.$set(this, 'active_calendar_event', null)
    },

    highlightDay(day) {
      this.$set(this, 'active_calendar_event', null)
      let date = day.id
      let events = []
      this.posts.forEach((post) => {
        if (post.customData.event_start_at === date) {
          events.push(post)
        }
      })
      let active_day = {}
      active_day.date = day.id
      active_day.posts = events
      active_day.day = day.day
      this.$set(this, 'active_calendar_day', active_day)
    },

    activateEventHover(event) {
      this.$set(this, 'active_hovered_event', event)
    },

    removeEventHover() {
      this.$set(this, 'active_hovered_event', null)
    },

    // Helper function to get the suffix for the day of the month
    getDaySuffix(day) {
      switch (day % 10) {
        case 1:
          return day !== 11 ? 'st' : 'th'
        case 2:
          return day !== 12 ? 'nd' : 'th'
        case 3:
          return day !== 13 ? 'rd' : 'th'
        default:
          return 'th'
      }
    },
  },
}
</script>

<style scoped>
.cloud-calendar .event-end-date,
.cloud-calendar .cloud-event-active {
  background: linear-gradient(200deg, #ff4778 0%, rgba(255, 71, 120, 0) 71.34%),
    #6f36bc !important;
}

.custom-calendar-overlay::after {
  content: '';
  position: absolute;
  left: 0px;
  bottom: 0px;
  width: 100%;
  height: 100%;
  background: black;
  opacity: 0.3;
  pointer-events: none;
  z-index: 5;
}

.calendar-modal {
  top: 0%;
  left: 50%;
  transform: translateX(-50%);
  height: 60%;
}

.calendar-modal.active {
  top: 10%;
  left: 50%;
  transform: translateX(-50%);
  height: 80%;
}

.event-title {
  display: -webkit-box;
  -webkit-line-clamp: 1;
  -webkit-box-orient: vertical;
  overflow: hidden;
  padding-left: 10px;
  margin-left: 1px;
  position: relative;
}

.event-title::after,
.date-circle::after {
  content: '';
  position: absolute;
  left: 0px;
  bottom: 40%;
  width: 4px;
  height: 4px;
  border-radius: 500px;
  background: var(--primary);
}

.event-end-date,
.event-range {
  opacity: 0;
}

.end-date-active .event-end-date {
  opacity: 1;
}

.range-active .event-range {
  opacity: 0.2;
}

.day-label {
  transform: translateY(0);
}

.day-label.active {
  transform: translateY(5px);
}

/deep/ .custom-calendar.vc-container {
  --day-border: 1px solid #b8c2cc;
  --day-border-highlight: 1px solid #b8c2cc;
  --day-width: 90px;
  --day-height: 90px;
  --weekday-bg: #f8fafc;
  --weekday-border: 1px solid #eaeaea;
  border-radius: 0;
  width: 100%;
}
/deep/ .custom-calendar.vc-container .vc-header {
  background-color: #f1f5f8;
  padding: 10px 0;
}

/deep/ .custom-calendar.vc-container .vc-weeks {
  padding: 0;
}
/deep/ .custom-calendar.vc-container .vc-weekday {
  background-color: var(--weekday-bg);
  border-bottom: var(--weekday-border);
  border-top: var(--weekday-border);
  padding: 5px 0;
}
/deep/ .custom-calendar.vc-container .vc-day {
  text-align: left;
  height: 100px;
  min-width: var(--day-width);
  background-color: white;
}
/deep/ .custom-calendar.vc-container .vc-day.weekday-1,
/deep/ .custom-calendar.vc-container .vc-day.weekday-7 {
  background-color: #eff8ff;
}
/deep/ .custom-calendar.vc-container .vc-day:not(.on-bottom) {
  border-bottom: var(--day-border);
}
/deep/ .custom-calendar.vc-container .vc-day:not(.on-bottom).weekday-1 {
  border-bottom: var(--day-border-highlight);
}
/deep/ .custom-calendar.vc-container .vc-day:not(.on-right) {
  border-right: var(--day-border);
}
/deep/ .custom-calendar.vc-container .vc-day-dots {
  margin-bottom: 5px;
}

/deep/ .vc-day.is-today {
  border: 2px dashed var(--primary) !important;
}

/* Cloud Overrides */
/deep/ .cloud-calendar.custom-calendar.vc-container .vc-header {
  background-color: var(--cw-900);
  padding: 10px 0;
}
/deep/ .cloud-calendar.custom-calendar.vc-container .vc-title {
  color: #fff;
}
/deep/ .cloud-calendar.custom-calendar.vc-container .vc-arrow {
  color: #fff;
}
/deep/ .cloud-calendar.custom-calendar.vc-container .vc-day.weekday-1,
/deep/ .cloud-calendar.custom-calendar.vc-container .vc-day.weekday-7 {
  background-color: var(--cw-50);
}
</style>
